import type { HowieCompanionReport, HowieCompanionSession, HowieCompanionSnippet } from '../services/supabase';

const PLATFORM_LABELS: Record<string, string> = {
  zoom: 'Zoom',
  teams: 'Microsoft Teams',
  slack: 'Slack',
  meet: 'Google Meet',
};

type SessionLike = Pick<HowieCompanionSession, 'metadata'>;

function meetingMetadata(session: SessionLike): Record<string, unknown> | null {
  const meeting = session.metadata?.meeting;
  return meeting && typeof meeting === 'object' ? (meeting as Record<string, unknown>) : null;
}

/** True for sessions from HOWIE Companion for Mac or Windows (metadata.meeting.client === 'desktop'). */
export function isDesktopCompanionSession(session: SessionLike): boolean {
  return meetingMetadata(session)?.client === 'desktop';
}

/**
 * '<Platform> · Mac app' or '<Platform> · Windows app' for desktop sessions
 * ('Mac app' alone for a desktop Ask with no meeting); null for extension sessions.
 */
export function companionDesktopLabel(session: SessionLike): string | null {
  const meeting = meetingMetadata(session);
  if (!meeting || meeting.client !== 'desktop') return null;
  const app = meeting.os === 'windows' ? 'Windows app' : 'Mac app';
  const platform = typeof meeting.platform === 'string' ? PLATFORM_LABELS[meeting.platform] : undefined;
  return platform ? `${platform} · ${app}` : app;
}

/**
 * A session that ended with nothing in it: no advice (every Ask sets `last_advice`) and no report.
 * The desktop app closes a meeting's session this way on Stop and clear (`/takes intent:'end'`).
 */
export function isEmptyEndedCompanionSession(
  session: Partial<Pick<HowieCompanionSession, 'status' | 'report_status' | 'last_advice'>>,
): boolean {
  return session.status === 'ended' && !session.last_advice && (session.report_status ?? 'none') === 'none';
}

/** The History sidebar's second line when a session has no advice yet. */
export function companionPreviewFallback(
  session: Pick<HowieCompanionSession, 'surface' | 'metadata'> & Partial<Pick<HowieCompanionSession, 'status' | 'report_status' | 'last_advice'>>,
): string {
  if (isEmptyEndedCompanionSession(session)) return 'No notes';
  if (isDesktopCompanionSession(session)) return 'Meeting coaching';
  if (session.surface === 'meet') return 'Live meeting coaching';
  if (session.surface === 'gmail') return 'Email coaching';
  return 'Browser coaching';
}

/** A meeting take is written by the desktop app when a meeting ends; other reports are the preliminary template. */
export function isMeetingTake(report: Pick<HowieCompanionReport, 'metadata'> | null | undefined): boolean {
  return report?.metadata?.kind === 'meeting_take';
}

/** Only real web pages get an "Open Source" link; desktop sessions carry a howie-desktop:// placeholder. */
export function isOpenableCompanionUrl(url: string | null | undefined): url is string {
  return typeof url === 'string' && /^https?:\/\//i.test(url);
}

/**
 * The word shown next to a retained snippet. Live meeting lines are stored with
 * source 'caption' (the column's CHECK predates tab and system audio); when the
 * line came from HOWIE listening rather than scraped captions, call it what it is.
 */
export function companionSnippetSourceLabel(snippet: Pick<HowieCompanionSnippet, 'source' | 'metadata'>): string {
  if (snippet.source === 'caption') {
    const transcriptSource = snippet.metadata?.transcript_source;
    return typeof transcriptSource === 'string' && transcriptSource !== 'meet-dom' ? 'transcript' : 'caption';
  }
  return snippet.source;
}
